import { useEffect, useCallback, memo, useState } from 'react'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import ContactTableRow from './ContactTableRow'
import ContactBulkActions from './ContactBulkActions'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AdminContactsSection')

const AdminContactsSection = memo(function AdminContactsSection({ onBack, onStatsUpdate, initialFilter = 'all' }) {
  const { execute } = useAsyncOperation()
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: contacts,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        // Hook provides params with: { page, limit, search?, filter? }
        const apiParams = { ...params }
        
        // Map filter to status or type
        if (params.filter && params.filter !== 'all') {
          if (params.filter === 'vendor') {
            apiParams.type = 'vendor'
          } else if (params.filter === 'report') {
            apiParams.contact_type = 'report'
          } else {
            apiParams.status = params.filter
          }
          delete apiParams.filter
        }
        
        const response = await adminAPI.getContacts(apiParams)
        return {
          data: response.data?.data || [],
          pagination: {
            page: response.data?.pagination?.page || 1,
            totalPages: response.data?.pagination?.totalPages || 1,
            total: response.data?.pagination?.total || 0
          }
        }
      })
    },
    { pageSize: 20, initialFilter }
  )

  const updateContactStatus = useCallback(async (id, status, notes = '') => {
    try {
      await adminAPI.updateContactStatus(id, { status, notes })
      refetch()
      if (onStatsUpdate) {
        onStatsUpdate() // Refresh dashboard stats
      }
    } catch (error) {
      logger.error('Failed to update contact', error)
    }
  }, [refetch, onStatsUpdate])

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      spam: 'bg-red-100 text-red-800'
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  const getVendorIntentLabel = (intent) => {
    const labels = {
      add: 'Add Listing',
      update: 'Update Listing',
      claim: 'Claim Ownership',
      confirm: 'Confirm Details',
      delete: 'Remove Listing',
      general: 'General Inquiry'
    }
    return labels[intent] || intent
  }

  const filterOptions = [
    { value: 'all', label: 'All Contacts' },
    { value: 'pending', label: 'Pending' },
    { value: 'vendor', label: 'Vendor Inquiries' },
    { value: 'report', label: 'Reports' },
    { value: 'responded', label: 'Responded' },
    { value: 'archived', label: 'Archived' }
  ]

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(contacts.map(c => c.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [contacts])

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} contact(s)?`)) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => adminAPI.deleteContact(id)))
      setSelectedIds(new Set())
      refetch()
      if (onStatsUpdate) onStatsUpdate()
    }, { successMessage: `${selectedIds.size} contact(s) deleted successfully` })
  }, [selectedIds, refetch, onStatsUpdate, execute])

  const handleBulkMarkResolved = useCallback(async () => {
    if (selectedIds.size === 0) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => 
        adminAPI.updateContactStatus(id, { status: 'responded' })
      ))
      setSelectedIds(new Set())
      refetch()
      if (onStatsUpdate) onStatsUpdate()
    }, { successMessage: `${selectedIds.size} contact(s) marked as resolved` })
  }, [selectedIds, refetch, onStatsUpdate, execute])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Contact Submissions</h2>
        <button
          onClick={onBack}
          className="text-sm text-primary hover:text-primary-600"
        >
          ← Back to Overview
        </button>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search by name, email, subject, or message..."
        onSearchChange={handleSearchChange}
        filterValue={filter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="contacts"
        onPageChange={handlePageChange}
      />

      {/* Bulk Actions Bar */}
      <ContactBulkActions
        selectedCount={selectedIds.size}
        onMarkResolved={handleBulkMarkResolved}
        onDelete={handleBulkDelete}
        onClear={() => setSelectedIds(new Set())}
      />

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={contacts.length > 0 && selectedIds.size === contacts.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">Loading contacts...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-red-500">{error}</td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">
                  No contact submissions yet
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <ContactTableRow
                  key={contact.id}
                  contact={contact}
                  isSelected={selectedIds.has(contact.id)}
                  onSelect={handleSelectOne}
                  onStatusUpdate={updateContactStatus}
                  getVendorIntentLabel={getVendorIntentLabel}
                  getStatusBadge={getStatusBadge}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminContactsSection
